// PreventiApp · Sprint 3 · Etapa 7 — Soporte INSIDE: Admin Colegio otorga/revoca;
// INSIDE consulta vigencia y registros visibles.
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  listColegios, getSoporteInfo, otorgarSoporte, revocarSoporte, listSoporteLog,
} from '../../data/supabaseAdmin';

const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-CL') : '—';

export default function AdminSoporte() {
  const { profile } = useAuth();
  const isInside = profile?.es_inside_staff === true;
  const isAdminCol = profile?.rol === 'admin_colegio';
  const colegioActivo = isAdminCol ? profile.colegio_id : null;

  const [colegios, setColegios] = useState([]);
  const [info, setInfo] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Form
  const [motivo, setMotivo] = useState('');
  const [hasta, setHasta] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0,16);
  });

  const targetColegio = colegioActivo || (colegios[0]?.id);

  const reload = async () => {
    setLoading(true); setError('');
    try {
      if (isInside) {
        const list = await listColegios();
        setColegios(list);
        if (list.length > 0) {
          const i = await getSoporteInfo(list[0].id);
          setInfo(i);
          setLog(await listSoporteLog(list[0].id));
        }
      } else if (colegioActivo) {
        setInfo(await getSoporteInfo(colegioActivo));
        setLog(await listSoporteLog(colegioActivo));
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const selectColegio = async (id) => {
    setLoading(true); setError('');
    try {
      setInfo(await getSoporteInfo(id));
      setLog(await listSoporteLog(id));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const onOtorgar = async () => {
    if (!info?.id) return;
    if (motivo.trim().length < 10) { alert('El motivo debe tener al menos 10 caracteres.'); return; }
    setBusy(true); setError('');
    try {
      await otorgarSoporte(info.id, {
        motivo: motivo.trim(),
        vigente_hasta: new Date(hasta).toISOString(),
        actor_id: profile.id,
      });
      setMotivo('');
      await selectColegio(info.id);
    } catch (e) { setError(`No se pudo otorgar: ${e.message}`); }
    finally { setBusy(false); }
  };

  const onRevocar = async () => {
    if (!info?.id) return;
    if (!window.confirm('¿Revocar el acceso INSIDE? Inside dejará de ver los registros operativos.')) return;
    setBusy(true); setError('');
    try {
      await revocarSoporte(info.id);
      await selectColegio(info.id);
    } catch (e) { setError(`No se pudo revocar: ${e.message}`); }
    finally { setBusy(false); }
  };

  const vigente = info?.permite_soporte_inside_hasta && new Date(info.permite_soporte_inside_hasta) > new Date();

  return (
    <div className="p-8 max-w-4xl" data-testid="page-soporte">
      <div className="mb-5">
        <h1 className="text-2xl font-extrabold text-[#0B1B5E]">Soporte INSIDE</h1>
        <p className="text-xs text-[#475472] mt-1">
          {isAdminCol
            ? 'Como Admin Colegio puedes otorgar acceso temporal a INSIDE para apoyo operativo.'
            : isInside
              ? 'Como INSIDE, ves los colegios con soporte vigente.'
              : 'Sólo el Admin Colegio puede otorgar o revocar el acceso INSIDE. Aquí ves el historial.'}
        </p>
      </div>

      {error && <div className="mb-4 p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B] text-sm" data-testid="soporte-error">{error}</div>}

      {isInside && colegios.length > 0 && (
        <div className="mb-5">
          <label className="text-xs font-bold text-[#475472] uppercase">Colegio</label>
          <select onChange={(e) => selectColegio(e.target.value)} data-testid="soporte-colegio-select"
                  className="mt-1 w-full md:w-1/2 px-3 py-2 rounded-xl border border-[#DDE6F7] text-sm">
            {colegios.map((c) => <option key={c.id} value={c.id}>{c.nombre} · {c.rbd}</option>)}
          </select>
        </div>
      )}

      {info && (
        <div className="bg-white border border-[#E5EAF5] rounded-2xl p-6 mb-5" data-testid="soporte-info">
          <p className="text-xs font-bold text-[#475472] uppercase">{info.nombre}</p>
          <div className="mt-2 flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${vigente ? 'bg-[#FFF1D6] text-[#7A4A0B]' : 'bg-[#F4F7FE] text-[#475472]'}`} data-testid="soporte-estado">
              {vigente ? 'Vigente' : 'Sin acceso'}
            </span>
            {vigente && <span className="text-xs text-[#475472]">hasta {fmt(info.permite_soporte_inside_hasta)}</span>}
          </div>
          {vigente && info.permite_soporte_inside_motivo && (
            <p className="mt-2 text-xs text-[#475472]"><span className="font-bold">Motivo:</span> {info.permite_soporte_inside_motivo}</p>
          )}

          {isAdminCol && (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-bold text-[#0B1B5E]">Motivo (mín. 10 chars)</span>
                <textarea data-testid="soporte-input-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
                          placeholder="Ej: Soporte por incidente crítico el 12/03/2026" className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]" />
              </label>
              <label className="block text-xs">
                <span className="font-bold text-[#0B1B5E]">Vigente hasta</span>
                <input type="datetime-local" data-testid="soporte-input-hasta" value={hasta} onChange={(e) => setHasta(e.target.value)}
                       className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]" />
                <div className="mt-4 flex gap-2">
                  <button onClick={onOtorgar} disabled={busy} data-testid="soporte-btn-otorgar"
                          className="px-4 py-2 rounded-xl bg-[#5346E6] hover:bg-[#0B1B5E] text-white text-sm font-bold disabled:opacity-60">
                    {vigente ? 'Renovar acceso' : 'Otorgar acceso'}
                  </button>
                  {vigente && (
                    <button onClick={onRevocar} disabled={busy} data-testid="soporte-btn-revocar"
                            className="px-4 py-2 rounded-xl border border-[#E64545] text-[#7C1F1F] text-sm font-bold disabled:opacity-60">
                      Revocar
                    </button>
                  )}
                </div>
              </label>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-[#E5EAF5] rounded-2xl p-6">
        <p className="text-xs font-bold text-[#475472] uppercase mb-3">Historial</p>
        <ul className="space-y-3" data-testid="soporte-log">
          {log.map((l) => (
            <li key={l.id} className="border-l-4 border-[#5346E6]/40 pl-3 text-xs">
              <p className="font-bold text-[#0B1B5E]">{l.accion} · {fmt(l.creado_en)}</p>
              {l.vigente_hasta && <p className="text-[#475472]">Vigente hasta: {fmt(l.vigente_hasta)}</p>}
              {l.motivo && <p className="text-[#475472]">Motivo: {l.motivo}</p>}
            </li>
          ))}
          {log.length === 0 && <li className="text-xs text-[#475472]">Sin registros de soporte previos.</li>}
        </ul>
      </div>
    </div>
  );
}
